import { useQuery } from '@tanstack/react-query';
import axiosClient from 'api/config/axios';
import { memoryQueryClient } from 'providers/TanstackQuery';

/** A single Xendit/PayPal payment method offered by a provider. */
export type PaymentMethod = { code: string; icon_url: string | null; name: string };

/** A provider entry as returned by the `providers_detailed` field. */
export type PaymentProviderEntry = { driver: string; icon_url: string | null; methods: PaymentMethod[] };

/** Provider/method matrix grouped by currency code. */
export type ProvidersByCurrency = Record<string, PaymentProviderEntry[]>;

/**
 * A single selectable payment option presented to the user in the combined
 * method picker. Each option carries both the backend `driver` and the
 * optional Xendit sub-method code so the caller can forward them to the
 * pay / funding endpoints. Drivers without configured methods (e.g. PayPal)
 * produce one option with `paymentMethod === undefined` and the provider's
 * display label; drivers with configured methods (e.g. Xendit) expand into
 * one option per method using the method's own display name.
 */
export type PaymentOption = {
  driver: string;
  iconUrl: string | null;
  paymentMethod?: string;
  label: string;
};

type LegacyPaymentMethod = Omit<PaymentMethod, 'icon_url'> & { icon_url?: string | null };
type LegacyPaymentProviderEntry = Omit<PaymentProviderEntry, 'icon_url' | 'methods'> & {
  icon_url?: string | null;
  methods?: LegacyPaymentMethod[];
};
type LegacyProvidersByCurrency = Record<string, LegacyPaymentProviderEntry[]>;

/**
 * Hardcoded default matrix used when the endpoint is unavailable.
 *
 * The Xendit fallback includes the seed method list documented in the
 * backend API plan so the offline path does not push users into a
 * guaranteed 422 from the pay endpoint.
 */
export const DEFAULT_PAYMENT_PROVIDERS: ProvidersByCurrency = {
  PHP: [
    {
      driver: 'paypal',
      icon_url: null,
      methods: [],
    },
    {
      driver: 'xendit',
      icon_url: null,
      methods: [
        { code: 'CREDIT_CARD', icon_url: null, name: 'Credit Card' },
        { code: 'GCASH', icon_url: null, name: 'GCash' },
        { code: 'GRABPAY', icon_url: null, name: 'GrabPay' },
        { code: 'PAYMAYA', icon_url: null, name: 'PayMaya' },
        { code: 'QRPH', icon_url: null, name: 'QR Ph' },
      ],
    },
  ],
  USD: [
    {
      driver: 'paypal',
      icon_url: null,
      methods: [],
    },
  ],
};

/** Normalises provider payloads to keep icon fields stable. */
function normaliseProvidersByCurrency(providersByCurrency: LegacyProvidersByCurrency): ProvidersByCurrency {
  return Object.fromEntries(
    Object.entries(providersByCurrency).map(([currency, providers]) => [
      currency,
      providers.map((provider) => ({
        driver: provider.driver,
        icon_url: provider.icon_url ?? null,
        methods: (provider.methods ?? []).map((method) => ({
          code: method.code,
          icon_url: method.icon_url ?? null,
          name: method.name,
        })),
      })),
    ]),
  );
}

/**
 * Fetches the enabled payment providers grouped by currency from the backend.
 *
 * Reads the `providers_detailed` response field which carries the structured
 * `{ driver, methods[] }` shape. The legacy `providers` field is deprecated
 * and ignored — see the `X-Deprecated-Field: providers` response header
 * documentation in the API plan.
 *
 * On failure the hardcoded default matrix is returned so callers always
 * receive a usable result. This means React Query never sees a rejected
 * promise and `retry` has no effect — this is intentional. The catch
 * ensures data is never undefined regardless of endpoint availability.
 */
export async function getPaymentProviders(signal?: AbortSignal): Promise<ProvidersByCurrency> {
  try {
    const result = await axiosClient.query.get('/api/v1/escrow/payment-providers', { signal });
    const providersByCurrency = result.data.providers_detailed as LegacyProvidersByCurrency | undefined;

    if (!providersByCurrency) {
      return DEFAULT_PAYMENT_PROVIDERS;
    }

    return normaliseProvidersByCurrency(providersByCurrency);
  } catch {
    return DEFAULT_PAYMENT_PROVIDERS;
  }
}

export const getPaymentProvidersQueryKey = () => ['escrow', 'payment-providers'] as const;

/**
 * React Query hook that fetches the enabled payment providers per currency.
 *
 * Falls back silently to a hardcoded default matrix when the endpoint returns
 * a 404, 5xx, or network error — no user-facing error is surfaced.
 *
 * placeholderData provides defaults immediately while the first fetch is
 * in-flight, and the try/catch in getPaymentProviders ensures defaults are
 * also returned after a failed fetch. Together this ensures data is never
 * undefined regardless of loading or error state.
 */
export function useGetPaymentProvidersQuery() {
  return useQuery<ProvidersByCurrency>(
    {
      queryKey: getPaymentProvidersQueryKey(),
      queryFn: ({ signal }) => getPaymentProviders(signal),
      placeholderData: DEFAULT_PAYMENT_PROVIDERS,
      staleTime: 1000 * 60 * 5,
      throwOnError: false,
    },
    memoryQueryClient,
  );
}
